import { Box, Button, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Editor from '@monaco-editor/react';
import type { OnValidate } from '@monaco-editor/react';
import { useCallback, useState } from 'react';

/**
 * The parts every JSON editor in this repo had its own copy of.
 *
 * There are four Monaco editors here — the OpenAPI editor, the raw config JsonEditor, the
 * step-function definition editor and the scaffolder's PlaceholderAwareJson — and the `<Editor>`
 * invocation, the marker-to-message formatting and the error list were byte-identical in all of
 * them. Three also shared the templatize suggestion panel. Only the validation RULES differ per
 * editor, so those stay with each caller; this module holds the chrome.
 */

const useStyles = makeStyles(theme => ({
  // Capped + scrollable: Monaco can emit dozens of markers for one bad brace, and an uncapped list
  // pushed the editor off screen. The raw-config editor already did this; now all four do.
  errorAlert: { marginBottom: theme.spacing(1), maxHeight: 130, overflowY: 'auto' },
  errorList: { margin: 0, paddingLeft: theme.spacing(2.5) },
  editorContainer: { border: `1px solid ${theme.palette.divider}`, borderRadius: 4 },
  actionColumn: { marginLeft: 12 },
}));

/**
 * Monaco marker -> the message string every editor here renders.
 *
 * `code` is typed `string | number | { value, target } | undefined`. The previous inline copies
 * interpolated it directly, so the object form rendered as "Code [object Object]"; take `.value`.
 */
export function formatMarker(marker: {
  code?: string | number | { value: string } | undefined;
  message: string;
  startLineNumber: number;
  startColumn: number;
}): string {
  const code =
    marker.code && typeof marker.code === 'object' ? marker.code.value : marker.code ?? '';
  return `Code ${code} | ${marker.message} [Ln ${marker.startLineNumber}, Col ${marker.startColumn}]`;
}

export interface JsonMonacoEditorProps {
  value: string;
  onChange: (value: string | undefined) => void;
  /** Monaco's own JSON syntax errors, already formatted. */
  onSyntaxErrors: (errors: string[]) => void;
  /** 4 for the raw config editor, 2 everywhere else. */
  tabSize?: number;
  height?: string;
}

export const JsonMonacoEditor = ({
  value,
  onChange,
  onSyntaxErrors,
  tabSize = 2,
  height = '500px',
}: JsonMonacoEditorProps) => {
  const classes = useStyles();
  const handleValidate: OnValidate = useCallback(
    markers => onSyntaxErrors(markers.map(formatMarker)),
    [onSyntaxErrors],
  );

  return (
    <div className={classes.editorContainer}>
      <Editor
        height={height}
        defaultLanguage="json"
        value={value}
        onChange={onChange}
        onValidate={handleValidate}
        theme="vs-dark"
        options={{
          minimap: { enabled: false },
          fontSize: 14,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          formatOnPaste: true,
          // Auto-format while typing fights the user's cursor in a large document.
          formatOnType: false,
          tabSize,
        }}
      />
    </div>
  );
};

export const ValidationErrorsAlert = ({
  errors,
  heading = 'Fix the following issues before continuing:',
}: {
  errors: string[];
  heading?: string;
}) => {
  const classes = useStyles();
  if (errors.length === 0) return null;
  return (
    <Alert severity="error" className={classes.errorAlert}>
      {heading}
      <ul className={classes.errorList}>
        {errors.map(e => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </Alert>
  );
};

export interface TemplatizeReplacementLike {
  from: string;
  to: string;
  count: number;
}

/**
 * The Templatize / Undo panel. `show` stays with the caller because the three callers disagree:
 * the OpenAPI and step-function-definition editors offer the panel whenever templatizing would
 * change the text at all (so pure reformatting is offerable, hence the "Standardized JSON
 * formatting" branch below), while the scaffolder's PlaceholderAwareJson only offers it when there
 * is at least one replacement rule. Folding those into one condition would change behaviour.
 */
export const TemplatizeSuggestionAlert = ({
  show,
  replacements,
  onApply,
  onUndo,
  canUndo,
}: {
  show: boolean;
  replacements: TemplatizeReplacementLike[];
  onApply: () => void;
  onUndo: () => void;
  canUndo: boolean;
}) => {
  const classes = useStyles();
  if (!show) return null;
  return (
    <Alert severity="info" style={{ marginBottom: 8, marginTop: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          {replacements.length > 0 ? (
            <>
              Found {replacements.length} templatize rule{replacements.length === 1 ? '' : 's'} that
              can be applied.
              <ul className={classes.errorList} style={{ marginTop: 6 }}>
                {replacements.slice(0, 6).map(r => (
                  <li key={`${r.from}->${r.to}`}>
                    {r.from} → {r.to} (×{r.count})
                  </li>
                ))}
                {replacements.length > 6 && <li>…and more</li>}
              </ul>
            </>
          ) : (
            <>Standardized JSON formatting can be applied.</>
          )}
        </Box>
        <Box display="flex" flexDirection="column" className={classes.actionColumn}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onApply}
            style={{ marginBottom: 6 }}
          >
            Templatize
          </Button>
          <Button variant="outlined" size="small" onClick={onUndo} disabled={!canUndo}>
            Undo
          </Button>
        </Box>
      </Box>
    </Alert>
  );
};

/**
 * Apply-templatize with a single-step undo. Identical in all three editors that offer it: remember
 * the pre-templatize text, and refuse both operations when there is nothing to change.
 */
export function useTemplatizeToggle(
  value: string,
  templatizedText: string,
  setValue: (next: string) => void,
) {
  const [previous, setPrevious] = useState<string | null>(null);

  const apply = useCallback(() => {
    if (!value.trim() || templatizedText === value) return;
    setPrevious(value);
    setValue(templatizedText);
  }, [value, templatizedText, setValue]);

  const undo = useCallback(() => {
    if (previous === null) return;
    const prev = previous;
    setPrevious(null);
    setValue(prev);
  }, [previous, setValue]);

  return { apply, undo, canUndo: previous !== null };
}
